import React, { createContext, useContext, useEffect, useState } from 'react';
import { io } from 'socket.io-client';
import { useAuth } from './AuthContext';
import { toast } from 'react-toastify';

const SocketContext = createContext();

export function useSocket() {
  const context = useContext(SocketContext);
  if (!context) {
    throw new Error('useSocket must be used within a SocketProvider');
  }
  return context;
}

export function SocketProvider({ children }) {
  const [socket, setSocket] = useState(null);
  const [connected, setConnected] = useState(false);
  const { user } = useAuth();

  useEffect(() => {
    if (user) {
      // Initialize socket connection - use REACT_APP_SOCKET_URL directly
      const socketURL = process.env.REACT_APP_SOCKET_URL || 'http://localhost:5001';

      const newSocket = io(socketURL, {
        auth: {
          token: localStorage.getItem('token')
        },
        transports: ['websocket', 'polling']
      });

      newSocket.on('connect', () => {
        console.log('Socket connected:', newSocket.id);
        setConnected(true);

        // Join user-specific rooms
        newSocket.emit('join', {
          userId: user.id,
          role: user.role
        });
      });

      newSocket.on('disconnect', () => {
        console.log('Socket disconnected');
        setConnected(false);
      });

      newSocket.on('connect_error', (error) => {
        console.error('Socket connection error:', error);
        setConnected(false);
      });

      // 2D task notification listeners
      newSocket.on('project:2d_assigned', (data) => {
        toast.success(`You've been assigned to project ${data.projectNumber}`, {
          autoClose: 5000,
          onClick: () => window.location.href = `/projects/${data.projectId}`
        });
      });

      newSocket.on('project:2d_amendment_required', (data) => {
        toast.warning(`Amendment required for project ${data.projectNumber}`, {
          autoClose: 8000,
          onClick: () => window.location.href = `/projects/${data.projectId}`
        });
      });

      setSocket(newSocket);

      // Cleanup on unmount
      return () => {
        newSocket.close();
      };
    }
  }, [user]);

  const value = {
    socket,
    connected
  };

  return (
    <SocketContext.Provider value={value}>
      {children}
    </SocketContext.Provider>
  );
}
